/* eslint-disable array-callback-return */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, TextField } from '@material-ui/core';
import { Autocomplete, useGlobal, globalizationPath, SelectCustom } from '@rcg/ui-react';
import { useFormik } from 'formik';
import { navigate } from '@gatsbyjs/reach-router';
import * as yup from 'yup';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';
import Button from '../UiComponents/Button';
import AirLoader from '../AirLoader';
import Dictionary from '../../utils/dictionary';
import Cruise from '../../assets/icons/cruise';
import Theme from '../../utils/theme';
import useStyles from './useStyles';
import setSearchResults from '../../operations/localState/setSearchResults';
import setSearchType from '../../operations/localState/setSearchType';
import {
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  SESSION_STORAGE_BOOKING_TOKEN_NAME,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
  SESSION_STORAGE_AIR_REDIRECT_ERROR,
  SHOW_CH_ERROR_BANNER,
  ENABLE_CRUISE_BOOKING_TYPE_FLAG,
  APPLICATION_NAME,
  REDIRECTION_MANAGE,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
  BANNER_TYPES,
} from '../../utils/constants';
import {
  prefixPathVar,
  bookingProfileVar,
  searchParamsVar,
} from '../../operations/localState/reactiveVariables';
import {
  IS_LOGGED_IN,
  CRUISE_SEARCH_FLIGTHS,
  FLIGHT_PROFILE,
  RETRIEVE_BOOKING,
  LAUNCH_DARKLY_FLAGS_AIR,
  CURRENT_BOOKING,
} from '../../operations/queries';
import getBrand from '../../utils/getBrand';
import { setSearchParams } from '../../operations/localState/setSearchParams';
import {
  setBookingProfile,
  setBookingToken,
  setBookingType,
} from '../../operations/localState/setBookingProfile';
import { setSessionStorageItem } from '../../operations/sessionStorage/utils';
import setSelectedDeparture from '../../operations/localState/setSelectedDeparture';
import setSelectedReturn from '../../operations/localState/setSelectedReturn';
import setHeldReservation from '../../operations/localState/setHeldReservation';
import { getYears, getMonths, getDays } from '../../utils/dateObjetcs';
import { trackSuccessCruiseReservation, trackError } from '../../utils/analytics/browseTracking';
import commonUtils, { hasGuestUndefined, checkForSomeFlights } from '../../utils/common';
import { LDUser } from '../../utils/ldUser';
import { getGuests } from '../../utils/payment';

import useDictionary from '../../hooks/useDictionary';

const currentBrand = getBrand();
const FormReservation = ({ isMobile, initialData, handleErrorBanner }) => {
  const classes = useStyles();
  const {
    data: { lang, localizedDayjs, market, country: globalCountry },
  } = useGlobal();
  const client = useApolloClient();
  const getDictionary = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const basePath = globalizationPath(market, lang, '');

  const [airport, setAirport] = useState(null);
  const [ship, setShip] = useState('none');
  const [selectedYear, setSelectedYear] = useState('none');
  const [month, setMonth] = useState('none');
  const [day, setDay] = useState('none');
  const [airportRequiredError, setAirportRequiredError] = useState(false);
  const [shipRequiredError, setShipRequiredError] = useState(false);
  const [selectedYearRequiredError, setSelectedYearRequiredError] = useState(false);
  const [monthRequiredError, setMonthRequiredError] = useState(false);
  const [dayRequiredError, setDayRequiredError] = useState(false);
  const [loadingSearchFlight, setLoadingSearchFlight] = useState(false);
  const [searchPageRequestTimeout, setSearchPageRequestTimeout] = useState(false);
  const [searchPageRequestTimeoutCount, setSearchPageRequestTimeoutCount] = useState(0);
  const [retrieveBookingData, setRetrieveBookingData] = useState();
  const [retrieveBookingError, setRetrieveBookingError] = useState();
  const [trackingParams, setTrackingParams] = useState(null);
  const [searchValues, setSearchValues] = useState(null);
  const bookingValidationError = useRef();

  const isCelebrityBrand = currentBrand.id === 'cel';

  const getCelebrityUrl = (redirectionType) => {
    const { bookingId, lastName, sailDate } = searchValues;
    return `${
      redirectionType === REDIRECTION_MANAGE
        ? NAVIGATION_PATHS.manageVacations
        : NAVIGATION_PATHS.timeline
    }?bid=${bookingId}&sdt=${sailDate}&scd=${ship}&lnm=${lastName}&gic=true`;
  };

  const getLinkInformation = (dictionary, redirectionType) => {
    return isCelebrityBrand ? getCelebrityUrl(redirectionType) : dictionary;
  };

  const urlTokenParam = /token=([^&]+)/i.exec(window.location.search);
  const urlBookingToken = Array.isArray(urlTokenParam) && urlTokenParam[1];
  if (urlBookingToken) {
    sessionStorage.setItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, urlBookingToken);
  }

  const searchPageTimeoutTime = 27000;

  const airports = initialData?.airports?.nodes || [];

  const ships =
    initialData?.ships
      .filter(({ code }) => {
        return initialData.searchSailings.shipSailings.some(
          (shipSailing) => shipSailing.shipCode === code,
        );
      })
      .map(({ name, code }) => {
        const shipName = name
          .toLowerCase()
          .replace(/(?:^|\s)(?!of|the)\S/g, (a) => a.toUpperCase());
        return { label: shipName, value: code };
      }) || [];

  const getFlightsBooked = (flightProfile) => {
    return flightProfile?.guests?.every(({ flightGuest }) => flightGuest?.guestFlights);
  };
  const selectedShipSailings = initialData?.searchSailings?.shipSailings.find(
    (item) => item.shipCode === ship,
  );

  const sailingDates = selectedShipSailings && selectedShipSailings.sailingDates;

  const yearsObj = sailingDates && getYears(sailingDates);
  const monthsObj = sailingDates && getMonths(sailingDates, selectedYear, localizedDayjs);
  const dates = sailingDates && getDays(sailingDates, selectedYear, month, localizedDayjs);

  const daysObj =
    dates &&
    dates.map((date) => {
      return { label: date, value: date };
    });

  const [
    executeRetrieveBooking,
    { loading: retrieveBookingLoading, data: retrieveBookData, error: retrieveBookError },
  ] = useLazyQuery(RETRIEVE_BOOKING, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  useEffect(() => {
    setRetrieveBookingData(retrieveBookData);
  }, [retrieveBookData]);

  useEffect(() => {
    setSessionStorageItem('search-values', { ...searchValues, ship });
  }, [searchValues]);

  useEffect(() => {
    setRetrieveBookingError(retrieveBookError);
  }, [retrieveBookError]);

  const [executeCurrentBooking, { loading: currentBookingLoading, called: currentBookingCalled }] =
    useLazyQuery(CURRENT_BOOKING, {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onCompleted: ({ currentBooking }) => {
        const { token } = currentBooking;
        sessionStorage.setItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, token);
        setRetrieveBookingData({
          retrieveBooking: {
            token,
            booking: { ...currentBooking },
          },
        });
      },
      onError: (error) => {
        setRetrieveBookingError(error);
      },
    });

  const [
    executeSearchQuery,
    {
      called: flightsSearchCalled,
      loading: flightsSearchLoading,
      data: flightsSearchData,
      error: flightsSearchError,
      refetch: flightsSearchRefetch,
    },
  ] = useLazyQuery(CRUISE_SEARCH_FLIGTHS, { fetchPolicy: 'network-only' });

  const [excecuteLaunchDarklyFlag, { data: ldFlagsData }] = useLazyQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    fetchPolicy: 'no-cache',
    variables: {
      ldUser: LDUser(),
      language: lang,
      clientApp: APPLICATION_NAME,
      application: APPLICATION_NAME,
      brand: getBrand().ldBrand,
      bookingCountry: globalCountry,
    },
  });

  const [executeFlightProfileQuery, { data: bookingProfileData, loading: bookingProfileLoading }] =
    useLazyQuery(FLIGHT_PROFILE, {
      variables: {
        limit: -1,
        ignoreCache: true,
      },
    });

  if (bookingProfileData) {
    setBookingProfile(bookingProfileData);
  }

  const saveBookingToken = (token) => {
    if (token) {
      sessionStorage.setItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, token);
      setBookingToken(token);
      client.writeQuery({ query: IS_LOGGED_IN, data: { isLoggedIn: true } });
    }
  };

  const currentTabTraking = 'airlookup';
  const bookingType = retrieveBookingData?.retrieveBooking?.booking?.type;

  const hasUndefinedGuest = hasGuestUndefined(
    retrieveBookingData?.retrieveBooking?.booking?.guests,
  );
  const handleErrorsMessages = (error) => {
    if (error && Array.isArray(error.graphQLErrors)) {
      // Invalid Booking Information --> Reuse error from Amend
      if (commonUtils.findErrorCode(error, ['BKNG-0017', 'CSE0293'])) {
        trackError('invalid', currentTabTraking);
        return handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorInvalidBookingInformationTitle.key,
          ),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorInvalidBookingInformationDescription.key,
          ),
        });
      }
      // When the booking is locked: Reuse error from Amend
      if (commonUtils.findErrorCode(error, 'CSE0098')) {
        trackError('booking locked', currentTabTraking);
        return handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorBookingTemporarilyLockedTitle.key,
          ),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorBookingTemporarilyLockedDescription.key,
          ),
        });
      }
      // [NEW] Bookings Outside the Date Range. This restriction will come from ACE. Old 5/330 rule
      if (commonUtils.findErrorCode(error, 'INCORRECT_SAILING_BOOKING_DATE')) {
        if (retrieveBookingData?.retrieveBooking) {
          const { token } = retrieveBookingData.retrieveBooking;
          saveBookingToken(token);
        }
        trackError('sailing date not supported', currentTabTraking);

        if (bookingType === 'TA') {
          return handleErrorBanner({
            title: getDictionary(
              Dictionary.airFlightBookFlightsReservationErrorSailingDateIsNotSupportedTitle.key,
            ),
            message: getDictionary(
              Dictionary.airFlightBookFlightsReservationTaErrorSailingDateNotSupportedDescription
                .key,
            ),
          });
        }
        return handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorSailingDateIsNotSupportedTitle.key,
          ),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorSailingDateIsNotSupportedDescription.key,
          ),
          type: 'warning',
        });
      }
      // [NEW] Invalid Booking Status (OF, CX) - For Cancelled Bookings
      if (commonUtils.findErrorCode(error, 'BOOKING_STATUS_CANCELLED')) {
        trackError('cancelled booking', currentTabTraking);
        return handleErrorBanner({
          title: getDictionary(Dictionary.airFlightBookFlightsReservationErrorCancelledTitle.key),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorCancelledDescription.key,
          ),
        });
      }
      // [NEW] Invalid Booking Countries (SGP , HKG, etc …)
      if (commonUtils.findErrorCode(error, 'FLIGHT_AGENCY_ERROR')) {
        if (retrieveBookingData?.retrieveBooking) {
          const { token } = retrieveBookingData.retrieveBooking;
          saveBookingToken(token);
        }
        trackError('country is not supported', currentTabTraking);
        if (bookingType === 'TA') {
          return handleErrorBanner({
            title: getDictionary(
              Dictionary.airFlightBookFlightsReservationErrorCruiseNonEligibleTitle.key,
            ),
            message: getDictionary(
              Dictionary.airFlightBookFlightsReservationTaErrorCruiseNonEligibleDescription.key,
            ),
          });
        }
        return handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorCruiseNonEligibleTitle.key,
          ),
          message: `${getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorCruiseNonEligibleDescription.key,
          )}`,
          linkLabel: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorCruiseNonEligibleDescriptionLinkText.key,
          ),
          linkDestination: getLinkInformation(
            getDictionary(
              Dictionary
                .airFlightBookFlightsReservationErrorSailingDateIsNotSupportedDescriptionLink.key,
            ),
            REDIRECTION_MANAGE,
          ),
          type: 'warning',
        });
      }
      // We couldn't find any flights
      if (commonUtils.findErrorCode(error, 'ACE-FS-1006')) {
        trackError('not found', currentTabTraking);

        return handleErrorBanner({
          title: getDictionary(Dictionary.airBookFlightNoResultsTitle.key),
          message: `${getDictionary(Dictionary.airBookFlightNoResultsDescription.key)} ${
            ldFlagsData?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]
          } ${getDictionary(Dictionary.airFlightSearchErrorAssistance.key)} `,
        });
      }

      if (commonUtils.findErrorCode(error, 'ACE-FS-1028')) {
        trackError('not found', currentTabTraking);

        return handleErrorBanner({
          title: getDictionary(Dictionary.airBookFlightNoResultsTitle.key),
          message: `${getDictionary(Dictionary.airBookFlightSearchErrorDescription.key)} ${
            ldFlagsData?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]
          } ${getDictionary(Dictionary.airFlightSearchErrorAssistance.key)} `,
        });
      }

      const { booking } = retrieveBookingData?.retrieveBooking || {};
      const { flightProfile } = booking || {};
      const hasAllGuestsFlights = getFlightsBooked(flightProfile);
      // Same airport error handling
      if (!hasAllGuestsFlights) {
        if (commonUtils.findErrorCode(error, 'SEARCH_SAME_AIRPORT_ERROR')) {
          trackError('Unsupported', currentTabTraking);
          return handleErrorBanner({
            title: getDictionary(Dictionary.airBrowsePageFormTypeLabelSameAirpotTitleError.key),
            message: getDictionary(Dictionary.airBrowsePageFormTypeLabelSameAirpotError.key),
          });
        }
        trackError('technical issue', currentTabTraking);
        return handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key,
          ),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
          ),
        });
      }
    }
    return false;
  };

  const showCHErrorMessage = (type) => {
    const { chMessagesBookingTypes } = SHOW_CH_ERROR_BANNER;
    return chMessagesBookingTypes.includes(type);
  };

  const redirectToCelDashboard = () => {
    const { bookingId, lastName, sailDate } = searchValues;
    window.location.assign(
      `${NAVIGATION_PATHS.timeline}?bid=${bookingId}&sdt=${sailDate}&scd=${ship}&lnm=${lastName}&gic=true`,
    );
  };

  const checkCourtesyHold = (bookingError, bookingData) => {
    if (bookingError && Array.isArray(bookingError.graphQLErrors)) {
      clearTimeout(searchPageRequestTimeout);
      setLoadingSearchFlight(false);
      const redirectErrors = ['BOOKING_STATUS_COURTESY_HOLD'];
      const errorFoundToAmendRedirect = commonUtils.findErrorCode(bookingError, redirectErrors);
      // If we found an error in this list "redirectErrors", we are going to redirect the user to
      // amend and show an error banner.
      if (errorFoundToAmendRedirect) {
        let showCHBanner = false;
        if (bookingData?.retrieveBooking) {
          const { token, booking } = bookingData.retrieveBooking;
          showCHBanner = showCHErrorMessage(booking.type);
          saveBookingToken(token);
        }
        if (!showCHBanner) {
          setSessionStorageItem(
            SESSION_STORAGE_AIR_REDIRECT_ERROR,
            errorFoundToAmendRedirect.extensions.code,
          );
          sessionStorage.setItem('air-ch', true);
          handleErrorBanner({
            title: getDictionary(Dictionary.airCourtesyHoldErrorTitle.key),
            message: getDictionary(Dictionary.airCourtesyHoldErrorDescription.key),
            linkLabel: getDictionary(
              Dictionary
                .airFlightBookFlightsReservationErrorSailingDateIsNotSupportedDescriptionLinkText
                .key,
            ),
            linkDestination: getLinkInformation(
              getDictionary(
                Dictionary
                  .airFlightBookFlightsReservationErrorSailingDateIsNotSupportedDescriptionLink.key,
              ),
              REDIRECTION_MANAGE,
            ),
            type: BANNER_TYPES.warning,
          });
        } else {
          handleErrorBanner({
            title: getDictionary(Dictionary.airCourtesyHoldErrorTitleTA.key),
            message: getDictionary(Dictionary.airCourtesyHoldErrorDescriptionTA.key),
            type: BANNER_TYPES.error,
          });
        }
        return true;
      }
      handleErrorsMessages(bookingError);
    }
    return false;
  };

  useEffect(() => {
    if (urlBookingToken) {
      executeCurrentBooking();
    }
  }, [urlBookingToken]);

  useEffect(() => {
    if (retrieveBookingData?.retrieveBooking) {
      const { country } = retrieveBookingData.retrieveBooking.booking;
      setSessionStorageItem('bookingCountry', {
        country: country?.code,
        officeID: country?.office?.code,
      });
      excecuteLaunchDarklyFlag({
        variables: {
          ldUser: LDUser(),
          language: lang,
          country: country?.code,
          clientApp: APPLICATION_NAME,
          application: APPLICATION_NAME,
          officeID: country?.office?.code,
          brand: currentBrand.ldBrand,
        },
      });
    }
  }, [retrieveBookingData]);

  useEffect(() => {
    if (retrieveBookingData?.retrieveBooking?.token) {
      saveBookingToken(retrieveBookingData.retrieveBooking.token);
    }

    if (hasUndefinedGuest) {
      setLoadingSearchFlight(false);
      handleErrorBanner({
        title: getDictionary(Dictionary.airTbaBannerTitle.key),
        message: `${getDictionary(Dictionary.airTbaBannerDescription.key)}`,
        linkLabel: getDictionary(Dictionary.airTbaBannerTextLink.key),
        linkDestination: getLinkInformation(
          `${basePath}${getDictionary(Dictionary.airTbaBannerDescriptionUrl.key)}`,
          REDIRECTION_MANAGE,
        ),
        type: 'warning',
      });
    } else if (retrieveBookingData?.retrieveBooking && !retrieveBookingError) {
      const { booking } = retrieveBookingData.retrieveBooking;

      const { flightProfile } = booking;
      const hasAllGuestsFlights = getFlightsBooked(flightProfile);
      if (hasAllGuestsFlights && ldFlagsData) {
        setLoadingSearchFlight(false);
        if (isCelebrityBrand) {
          redirectToCelDashboard();
        } else {
          setSessionStorageItem(SESSION_STORAGE_AIR_REDIRECT_ERROR, 'BOOKING_WITH_FLIGHT');
          window.location.assign(prefixPathVar() + NAVIGATION_PATHS.reservation);
        }
      }
      if (hasUndefinedGuest && !retrieveBookingError) {
        setLoadingSearchFlight(false);
        handleErrorBanner({
          title: getDictionary(Dictionary.airTbaBannerTitle.key),
          message: `${getDictionary(Dictionary.airTbaBannerDescription.key)}`,
          linkLabel: getDictionary(Dictionary.airTbaBannerTextLink.key),
          linkDestination: getLinkInformation(
            `${basePath}${getDictionary(Dictionary.airTbaBannerDescriptionUrl.key)}`,
          ),
          type: 'warning',
        });
      }
    } else if (
      retrieveBookingError &&
      commonUtils.findErrorCode(retrieveBookingError, 'ERROR_BOOKING_INVALID_TYPE')
    ) {
      setLoadingSearchFlight(false);
      handleErrorBanner({
        title: getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorUnsupportedBookingTitle.key,
        ),
        message: getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorUnsupportedBookingDescription.key,
        ),
      });
    } else if (retrieveBookingError) {
      bookingValidationError.current = checkCourtesyHold(retrieveBookingError, retrieveBookingData);
    }
    // eslint-disable-next-line
  }, [ldFlagsData, retrieveBookingError, retrieveBookingData]);

  useEffect(() => {
    const bookingHasSomeFlights = checkForSomeFlights(
      retrieveBookingData?.retrieveBooking?.booking?.flightProfile,
    );
    if (bookingHasSomeFlights) {
      navigate(`${urlPath}${NAVIGATION_PATHS.manage}`);
    }
  }, [retrieveBookingData]);

  useEffect(() => {
    if (ldFlagsData && !hasUndefinedGuest) {
      const { allowedBookingTypes } = ldFlagsData.ldFlags[ENABLE_CRUISE_BOOKING_TYPE_FLAG];
      let includeType = false;
      if (retrieveBookingData && !retrieveBookingError) {
        const { booking } = retrieveBookingData.retrieveBooking;
        setBookingType(booking.type);
        if (
          !(
            allowedBookingTypes.length === 0 ||
            (allowedBookingTypes[0] !== '*' &&
              booking.type &&
              !allowedBookingTypes.includes(booking.type))
          )
        ) {
          includeType = true;
        }
      }
      if (flightsSearchError && !retrieveBookingError && !retrieveBookingLoading && includeType) {
        clearTimeout(searchPageRequestTimeout);
        setLoadingSearchFlight(false);
        handleErrorsMessages(flightsSearchError);
      }
      if (!currentBookingLoading && !bookingValidationError.current && urlBookingToken) {
        navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
      }
    }
    // eslint-disable-next-line
  }, [
    ldFlagsData,
    flightsSearchLoading,
    retrieveBookingError,
    retrieveBookingLoading,
    hasUndefinedGuest,
    currentBookingLoading,
  ]);

  useEffect(() => {
    if (flightsSearchData && !hasUndefinedGuest && !retrieveBookingError) {
      setLoadingSearchFlight(false);
      executeFlightProfileQuery();
    }
  }, [
    flightsSearchData,
    executeFlightProfileQuery,
    retrieveBookingData,
    retrieveBookingError,
    hasUndefinedGuest,
  ]);

  useEffect(() => {
    if (flightsSearchCalled && !flightsSearchLoading && flightsSearchData?.flightsSearch) {
      clearTimeout(searchPageRequestTimeout);
      setLoadingSearchFlight(false);
      setSearchResults(
        {
          ...flightsSearchData.flightsSearch,
          flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
        },
        true,
      );

      if (bookingProfileData) {
        const searchParams = {
          departureAirportCode: airport,
          guestIDs: retrieveBookingData?.retrieveBooking?.booking?.guests?.map((guest) => guest.id),
          guests: bookingProfileData.flightProfile.guests,
          ship: ships.find((item) => item.value === ship),
        };
        setSearchParams(searchParams);

        trackSuccessCruiseReservation({
          ...trackingParams,
          reservationChannel: retrieveBookingData?.retrieveBooking?.booking?.type,
        });
        navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
      }
    }
    // eslint-disable-next-line
  }, [
    flightsSearchData,
    flightsSearchCalled,
    flightsSearchLoading,
    urlPath,
    bookingProfileData,
    retrieveBookingData,
    airport,
  ]);

  const validationSchema = yup.object({
    reservationNumber: yup
      .string(getDictionary(Dictionary.airFormReservationFieldNumberHelper.key))
      .matches(/^\d+$/, getDictionary(Dictionary.airFormReservationFieldNumberMatchError.key))
      .max(7, getDictionary(Dictionary.airFormReservationFieldNumberMaxError.key))
      .min(1, getDictionary(Dictionary.airFormReservationFieldNumberMaxError.key))
      .required(getDictionary(Dictionary.airFormReservationFieldNumberRequiredError.key)),
    lastName: yup
      .string(getDictionary(Dictionary.airFormReservationFieldLastNameHelper.key))
      .matches(
        /[a-zA-Z]+/g,
        getDictionary(Dictionary.airFormReservationFieldLastNameMatchError.key),
      )
      .required(getDictionary(Dictionary.airFormReservationFieldLastNameRequiredError.key)),
  });

  const isRequiredFormFieldsFilled =
    !airportRequiredError &&
    !shipRequiredError &&
    !selectedYearRequiredError &&
    !monthRequiredError &&
    !dayRequiredError;

  const validateRequiredFormFileds = () => {
    setAirportRequiredError(!airport);
    setShipRequiredError(ship === 'none');
    setSelectedYearRequiredError(selectedYear === 'none');
    setMonthRequiredError(month === 'none');
    setDayRequiredError(day === 'none');
  };

  const formik = useFormik({
    initialValues: {
      reservationNumber: '',
      lastName: '',
    },
    validationSchema,
    onSubmit: (values) => {
      if (isRequiredFormFieldsFilled) {
        setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
        setSelectedDeparture(null);
        setSelectedReturn(null);
        setHeldReservation(null);
        setSearchType({ tab: 'booked' });
        saveBookingToken(null);
        const valuesToSend = {
          bookingId: values.reservationNumber,
          lastName: values.lastName,
          sailDate: `${selectedYear}-${month}-${day}`,
          shipCode: ship,
        };
        setSearchValues(valuesToSend);
        executeRetrieveBooking({
          variables: valuesToSend,
        });
        setTrackingParams({
          myResShip: ship,
          myresSailDate: `${selectedYear}-${month}-${day}`,
          reservationNumber: values.reservationNumber,
        });
        const guests = getGuests(bookingProfileVar, searchParamsVar);
        const queryParams = {
          variables: {
            bookingID: values.reservationNumber,
            departureAirportCode: airport,
            guests,
          },
        };
        executeSearchQuery(queryParams);
        setLoadingSearchFlight(true);
        if (searchPageRequestTimeoutCount === 0) {
          /* istanbul ignore next */
          const searchPageRequestTimeoutVar = setTimeout(() => {
            flightsSearchRefetch(queryParams);
          }, searchPageTimeoutTime);
          setSearchPageRequestTimeout(searchPageRequestTimeoutVar);
        }
        setSearchPageRequestTimeoutCount(searchPageRequestTimeoutCount + 1);
      } else {
        validateRequiredFormFileds();
      }
    },
  });

  const placeholderColor = Theme.palette.gray[400];
  const activeColor = Theme.palette.gray[750];

  const shipsHandler = (event) => {
    setShip(event.target.value);
    setShipRequiredError(false);
    setSelectedYear('none');
    setMonth('none');
    setDay('none');
  };
  const yearHandler = (event) => {
    setSelectedYear(event.target.value);
    setSelectedYearRequiredError(false);
    setMonth('none');
    setDay('none');
  };
  const monthHandler = (event) => {
    setMonth(event.target.value);
    setMonthRequiredError(false);
    setDay('none');
  };
  const dayHandler = (event) => {
    setDay(event.target.value);
    setDayRequiredError(false);
  };

  const airportSelectHandler = (value) => {
    setAirport(value ? value.code : '');
    setAirportRequiredError(false);
  };

  return (
    <>
      <AirLoader
        open={
          loadingSearchFlight ||
          retrieveBookingLoading ||
          bookingProfileLoading ||
          (!retrieveBookingError && currentBookingCalled)
        }
        status={
          (retrieveBookingLoading || flightsSearchLoading || bookingProfileLoading) &&
          getDictionary(Dictionary.loaderRetrieveCruise.key)
        }
      />
      <Box
        component="form"
        noValidate
        // autoComplete="off"
        onSubmit={formik.handleSubmit}
        className={classes.rootFormReservation}>
        <Grid container className={classes.mainContainer}>
          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.textField}
              InputLabelProps={{ className: classes.styledLabel, shrink: true }}
              id="reservationNumber"
              name="reservationNumber"
              label={getDictionary(Dictionary.airFormReservationFieldNumberLabel.key)}
              value={formik.values.reservationNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.reservationNumber && Boolean(formik.errors.reservationNumber)}
              helperText={formik.touched.reservationNumber && formik.errors.reservationNumber}
              variant="outlined"
              inputProps={{
                inputMode: 'numeric',
                maxLength: 7,
                'aria-label': 'reservation-number',
              }}
              placeholder={getDictionary(Dictionary.airFormReservationFieldNumberPlaceholder.key)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              className={classes.textField}
              InputLabelProps={{ className: classes.styledLabel, shrink: true }}
              label={getDictionary(Dictionary.airFormReservationFieldLastnameLabel.key)}
              variant="outlined"
              placeholder={getDictionary(Dictionary.airFormReservationFieldLastnamePlaceholder.key)}
              id="lastName"
              name="lastName"
              inputProps={{
                'aria-label': 'last-name',
              }}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectCustom
              isMobile={isMobile}
              options={ships}
              icon={<Cruise />}
              selectLabel={getDictionary(Dictionary.airFormReservationFieldShipLabel.key)}
              placeholder={getDictionary(Dictionary.airFormReservationFieldShipPlaceholder.key)}
              currentValue={ship}
              selectClassName={classes.select}
              menuClassName={classes.menu}
              defaultColor={ship === 'none' ? placeholderColor : activeColor}
              handleOption={shipsHandler}
              dataTestid="ship-select"
              className={shipRequiredError ? 'select-custom-field-error' : ''}
              textHelperError={
                shipRequiredError
                  ? getDictionary(Dictionary.airFormReservationFieldShipRequiredError.key)
                  : ''
              }
            />
          </Grid>
          <Grid item container xs={12} sm={6} className={classes.dateInputs}>
            <legend>{getDictionary(Dictionary.airFormReservationFieldDatesLabel.key)}</legend>
            <div className="date-select-wrapper">
              <Grid item xs={4}>
                <SelectCustom
                  isMobile={isMobile}
                  options={yearsObj}
                  selectLabel=""
                  placeholder={getDictionary(Dictionary.airFormReservationFieldYearLabel.key)}
                  currentValue={selectedYear}
                  defaultColor={selectedYear === 'none' ? placeholderColor : activeColor}
                  handleOption={yearHandler}
                  dataTestid="year-select"
                  selectClassName={classes.dateSelect}
                  menuClassName={classes.menu}
                  disabled={ship === 'none'}
                  className={selectedYearRequiredError ? 'select-custom-field-error' : ''}
                  textHelperError={
                    selectedYearRequiredError
                      ? getDictionary(Dictionary.airFormReservationFieldYearRequiredError.key)
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <SelectCustom
                  isMobile={isMobile}
                  options={monthsObj}
                  selectLabel=""
                  placeholder={getDictionary(Dictionary.airFormReservationFieldMonthLabel.key)}
                  currentValue={month}
                  defaultColor={month === 'none' ? placeholderColor : activeColor}
                  handleOption={monthHandler}
                  selectClassName={classes.dateSelect}
                  menuClassName={classes.menu}
                  dataTestid="month-select"
                  disabled={selectedYear === 'none'}
                  className={monthRequiredError ? 'select-custom-field-error' : ''}
                  textHelperError={
                    monthRequiredError
                      ? getDictionary(Dictionary.airFormReservationFieldMonthRequiredError.key)
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <SelectCustom
                  isMobile={isMobile}
                  options={daysObj}
                  selectLabel=""
                  placeholder={getDictionary(Dictionary.airFormReservationFieldDayLabel.key)}
                  currentValue={day}
                  defaultColor={day === 'none' ? placeholderColor : activeColor}
                  handleOption={dayHandler}
                  selectClassName={classes.dateSelect}
                  menuClassName={classes.menu}
                  dataTestid="day-select"
                  disabled={month === 'none'}
                  className={dayRequiredError ? 'select-custom-field-error' : ''}
                  textHelperError={
                    dayRequiredError
                      ? getDictionary(Dictionary.airFormReservationFieldDayRequiredError.key)
                      : ''
                  }
                />
              </Grid>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              className={
                airportRequiredError ? `${classes.flightsInput} field-error` : classes.flightsInput
              }
              id="airports"
              options={airports}
              getInputLabel={commonUtils.fliyingFromOptions}
              getOptionLabel={commonUtils.fliyingFromOptions}
              getSelectedValue={(value) => airportSelectHandler(value)}
              placeholder={getDictionary(Dictionary.airFromPlaceholder.key)}
              label={getDictionary(Dictionary.airFormReservationFieldAirportsLabel.key)}
              noResultsErrorMessage={getDictionary(Dictionary.airNoResultsErrorMessage.key)}
            />
            {airportRequiredError && (
              <div className={classes.airpotRequiredError}>
                {getDictionary(Dictionary.airFormReservationFieldAirportRequiredError.key)}
              </div>
            )}
          </Grid>
        </Grid>
        <Grid item sm={12} className={classes.footerForm}>
          <Button
            type="submit"
            onClick={validateRequiredFormFileds}
            variant="contained"
            color="primary">
            {getDictionary(Dictionary.airFormReservationSubmitLabel.key)}
          </Button>
        </Grid>
      </Box>
    </>
  );
};

FormReservation.propTypes = {
  handleErrorBanner: PropTypes.func,
  isMobile: PropTypes.bool,
  initialData: {
    airports: {
      nodes: PropTypes.array,
    },
    searchSailings: {
      shipSailings: PropTypes.array,
    },
    ships: PropTypes.array,
  },
};

export default FormReservation;
